// 案件タブ v2（一覧・カンバン補助）の行モデル。判定そのものは lib/giftingRules（nextAction）が正本。
// 設計正本=tmp/fable/2026-10-30-gifting-ui-v2/design/handoff-README.md §3 ①②③。
import type { GfActionKey, GfCampaign, GfCreator, GfEntry, GfStage } from "../../../../types/gifting";
import {
  effectiveType,
  ACTION_KEYS,
  CAMPAIGN_TYPE_LABEL,
  STAGE_NAMES,
  deadlineInfo,
  fmtYmd,
  handleOf,
  mediaChip,
  nextActionsOf,
  primarySns,
  typeHasFee,
  type GfNextAction,
} from "../../../../lib/giftingRules";
import { cardInfo, type CardInfo, type PipelineCtx } from "./model";

export type Density = "normal" | "compact";
export type GroupBy = "none" | "camp" | "stage" | "act";
export type SavedFilterKey = "ig2" | "dl14" | "paid" | "nolink";

export interface ListRow {
  e: GfEntry;
  info: CardInfo;
  creator: GfCreator | null;
  campaign: GfCampaign | null;
  action: GfNextAction;
  /** 'IG 9.7万'（主媒体） */
  primaryMedia: string;
  /** 'IG' など主媒体の短縮名なしの @handle */
  handle: string;
  /** 検索用（小文字） */
  search: string;
}

/** 進行中のエントリー → 行。次のアクション順（sortKey 昇順・同点は名前順）に並べる */
export function buildRows(entries: readonly GfEntry[], ctx: PipelineCtx): ListRow[] {
  // 判定は一括版（同じ人の未返信は直近の1案件だけに付く）
  const actions = nextActionsOf(entries, ctx.campaigns, ctx.nowIso);
  const rows = entries.map<ListRow>((e) => {
    const info = cardInfo(e, ctx);
    const creator = ctx.creators.get(e.influencer_id) ?? null;
    const campaign = info.campaign;
    const p = creator ? primarySns(creator.sns) : null;
    const handles = creator ? creator.sns.map((s) => s.handle).join(" ") : "";
    return {
      e,
      info,
      creator,
      campaign,
      action: actions.get(e.id)!,
      primaryMedia: p ? mediaChip(p.platform, p.follower_count) : "",
      handle: handleOf(p),
      search: `${info.creatorName} ${handles} ${info.campaignName} ${e.product_name}`.toLowerCase(),
    };
  });
  return rows.sort((a, b) => a.action.sortKey - b.action.sortKey || a.info.creatorName.localeCompare(b.info.creatorName, "ja"));
}

// ---- 日付（JST 暦日） ----------------------------------------------------------------

function ymdMs(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Date.UTC(y, (m ?? 1) - 1, d ?? 1);
}

/** 暦日（JST）での「何日前か」。解釈できなければ null。floor 日数以上になるので、判定のしきい値より小さく見えることはない */
export function calDaysAgo(iso: string | null | undefined, nowIso: string): number | null {
  const a = fmtYmd(iso);
  const b = fmtYmd(nowIso);
  if (!a || !b) return null;
  return Math.max(0, Math.round((ymdMs(b) - ymdMs(a)) / 86_400_000));
}

/** 今日 / 昨日 / n日前 */
export function agoLabel(days: number | null): string {
  if (days === null) return "—";
  if (days <= 0) return "今日";
  if (days === 1) return "昨日";
  return `${days}日前`;
}

/** 'M/D'（JST） */
export function mdOf(iso: string | null | undefined): string {
  const ymd = fmtYmd(iso);
  if (!ymd) return "";
  return `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
}

// ---- 絞り込み -----------------------------------------------------------------------

export const SAVED_FILTERS: ReadonlyArray<{ key: SavedFilterKey; label: string }> = [
  { key: "ig2", label: "IG・返信待ち" },
  { key: "dl14", label: "期限14日以内" },
  { key: "paid", label: "有償案件" },
  { key: "nolink", label: "IG未連携" },
];

export function matchSaved(key: SavedFilterKey, row: ListRow, nowIso: string): boolean {
  const e = row.e;
  switch (key) {
    case "ig2":
      return e.ig_linked && e.stage === 2;
    case "dl14": {
      if (e.stage !== 5 || e.post_count > 0) return false;
      const dl = deadlineInfo(e, nowIso);
      return !!dl && dl.daysLeft <= 14;
    }
    case "paid":
      return typeHasFee(effectiveType(e, row.campaign));
    case "nolink":
      return !e.ig_linked;
  }
}

export function matchQuery(row: ListRow, q: string): boolean {
  const t = q.trim().toLowerCase();
  return !t || row.search.includes(t);
}

// ---- グループ -----------------------------------------------------------------------

export interface RowGroup {
  key: string;
  label: string;
  /** 案件タイプなど */
  sub: string;
  rows: ListRow[];
  /** tier 0〜4 の件数 */
  need: number;
  /** tier 0〜1（今日中）の件数 */
  hot: number;
}

function summarize(key: string, label: string, sub: string, rows: ListRow[]): RowGroup {
  return {
    key,
    label,
    sub,
    rows,
    need: rows.filter((r) => r.action.tier < 5).length,
    hot: rows.filter((r) => r.action.tier <= 1).length,
  };
}

export function groupRows(rows: ListRow[], by: GroupBy, campaigns: readonly GfCampaign[]): RowGroup[] {
  if (by === "none") return [summarize("all", "", "", rows)];
  const groups: RowGroup[] = [];
  const pick = (pred: (r: ListRow) => boolean) => rows.filter(pred);
  if (by === "camp") {
    const known = new Set(campaigns.map((c) => c.id));
    for (const c of campaigns) {
      const rs = pick((r) => r.e.campaign_id === c.id);
      if (rs.length) groups.push(summarize(`camp:${c.id}`, c.name, CAMPAIGN_TYPE_LABEL[c.type], rs));
    }
    const rest = pick((r) => !r.e.campaign_id || !known.has(r.e.campaign_id));
    if (rest.length) groups.push(summarize("camp:none", "案件なし", "", rest));
  } else if (by === "stage") {
    for (const s of [1, 2, 3, 4, 5, 6, 7, 8] as GfStage[]) {
      const rs = pick((r) => r.e.stage === s);
      if (rs.length) groups.push(summarize(`stage:${s}`, STAGE_NAMES[s], "", rs));
    }
  } else {
    for (const k of ACTION_KEYS as readonly GfActionKey[]) {
      const rs = pick((r) => r.action.key === k);
      if (rs.length) groups.push(summarize(`act:${k}`, rs[0].action.label, "", rs));
    }
  }
  return groups;
}
